import { validateSupplyNames, validateCatalog, type Catalog } from './domain';
import { emptyCatalog } from './seed';
import { validateCoffee } from './coffee';
import { validatePreparation } from './recipes';
import { validateDrinkGroups } from './drinkGroups';

export const saveCollections = ['ingredients', 'packaging', 'equipment', 'products', 'drinkGroups', 'settings'] as const;
export type CatalogSelection = { collection: typeof saveCollections[number]; id: string };
export const selectionKey = (item: CatalogSelection) => `${item.collection}:${item.id}`;
export function saveBaseline(published?: Catalog, approvedBrowseSettings?: Partial<Catalog['settings']>): Catalog {
  if (published) return structuredClone(published);
  const settings = structuredClone(emptyCatalog.settings);
  // Only approved public metadata carries into the first independent save.
  // Prices and ordering state always start from the safe baseline.
  for (const key of ['name', 'openingHours', 'pickupInstructions'] as const) {
    if (approvedBrowseSettings?.[key] !== undefined) (settings as any)[key] = structuredClone(approvedBrowseSettings[key]);
  }
  settings.paused = true;
  return { ...structuredClone(emptyCatalog), settings, ingredients: [], packaging: [], equipment: [], products: [], drinkGroups: [] };
}
export function selectedCatalog(draft: Catalog, published?: Catalog, selection?: CatalogSelection[], approvedBrowseSettings?: Partial<Catalog['settings']>, identityBaseline: Catalog | undefined = published) {
  if (selection === undefined) return { catalog: structuredClone(draft), requiredChanges: [] as CatalogSelection[], errors: validateCatalog(draft, identityBaseline) };
  const catalog = saveBaseline(published, approvedBrowseSettings);
  const errors: string[] = [];
  const requiredChanges: CatalogSelection[] = [];
  if (!selection.length) errors.push('Select at least one change to save.');
  const keys = new Set<string>();
  for (const item of selection) {
    if (!saveCollections.includes(item.collection) || typeof item.id !== 'string' || keys.has(selectionKey(item))) { errors.push('Invalid change selection.'); continue; }
    keys.add(selectionKey(item));
    if (item.collection === 'settings') {
      if (!Object.hasOwn(draft.settings, item.id) && !Object.hasOwn(catalog.settings, item.id)) { errors.push('The selected setting no longer exists.'); continue; }
      if (Object.hasOwn(draft.settings, item.id)) (catalog.settings as any)[item.id] = structuredClone((draft.settings as any)[item.id]);
      else delete (catalog.settings as any)[item.id];
    } else {
      const source = draft[item.collection]?.find(x => x.id === item.id);
      const target = (catalog[item.collection] ?? []) as any[];
      const at = target.findIndex(x => x.id === item.id);
      if (!source && at < 0) { errors.push('The selected item no longer exists.'); continue; }
      if (source) { if (at < 0) target.push(structuredClone(source)); else target[at] = structuredClone(source); }
      else target.splice(at, 1);
      (catalog as any)[item.collection] = target;
    }
  }
  // A first save never opens ordering, even when the working settings are open.
  if (!published) catalog.settings.paused = true;
  catalog.revision = Math.max(draft.revision, published?.revision ?? 0) + 1;
  const require = (collection: CatalogSelection['collection'], id: string) => {
    if (collection === 'settings') return;
    if (catalog[collection]?.some(x => x.id === id)) return;
    const item = { collection, id };
    if (!requiredChanges.some(x => selectionKey(x) === selectionKey(item)) && draft[collection]?.some(x => x.id === id)) requiredChanges.push(item);
  };
  for (const p of catalog.products.filter(p => !p.archived)) {
    for (const id of [...p.beanIds, ...p.ingredients.map(x => x.ingredientId), ...p.extras.flatMap(x => x.ingredients.map(i => i.ingredientId))]) require('ingredients', id);
    for (const x of p.packaging) require('packaging', x.packagingId);
    for (const id of [...p.equipmentIds, ...p.extras.flatMap(x => x.equipmentIds ?? [])]) require('equipment', id);
    if (p.ingredients.some(x => x.role === 'ice') && p.iceLevels.some(level => level !== 'none' && (p.iceQuantities[level] ?? 0) > 0)) require('equipment', 'ice_machine');
  }
  for (const g of catalog.drinkGroups ?? []) for (const id of Object.values(g.variants)) if (id) require('products', id);
  // Recipe membership changes must travel with the corresponding group, rather than
  // quietly making a saved Hot/Iced recipe standalone or retaining stale metadata.
  for (const item of selection.filter(x => x.collection === 'products')) {
    for (const group of draft.drinkGroups ?? []) if (Object.values(group.variants).includes(item.id)) {
      const live = catalog.drinkGroups?.find(g => g.id === group.id);
      if (!live || !Object.values(live.variants).includes(item.id)) {
        const needed: CatalogSelection = { collection: 'drinkGroups', id: group.id };
        if (!requiredChanges.some(x => selectionKey(x) === selectionKey(needed))) requiredChanges.push(needed);
      }
    }
  }
  if (requiredChanges.length) errors.push('Select the required related items before saving these changes.');
  errors.push(...validateCatalog(catalog, identityBaseline));
  return { catalog, requiredChanges, errors: [...new Set(errors)] };
}

// Structural checks apply to the edited records and recipes that use them. Missing
// purchase prices can remain in the working copy without blocking other editors.
export function draftUpdateScope(catalog: Catalog, selection: CatalogSelection[]): Catalog {
  const ingredientIds = new Set(selection.filter(x => x.collection === 'ingredients').map(x => x.id));
  const productIds = new Set(selection.filter(x => x.collection === 'products').map(x => x.id));
  const groups = (catalog.drinkGroups ?? []).filter(g => selection.some(s => s.collection === 'drinkGroups' && s.id === g.id) || Object.values(g.variants).some(id => productIds.has(id!)));
  for (const g of groups) for (const id of Object.values(g.variants)) if (id) productIds.add(id);
  for (const g of catalog.drinkGroups ?? []) if (!groups.includes(g) && Object.values(g.variants).some(id => productIds.has(id!))) groups.push(g);
  const products = catalog.products.filter(p => productIds.has(p.id) || p.beanIds.some(id => ingredientIds.has(id)) || [...p.ingredients, ...p.extras.flatMap(e => e.ingredients)].some(x => ingredientIds.has(x.ingredientId)));
  const usedIngredients = new Set([...ingredientIds, ...products.flatMap(p => [...p.beanIds, ...p.ingredients.map(i => i.ingredientId), ...p.extras.flatMap(e => e.ingredients.map(i => i.ingredientId))])]);
  return { ...catalog, ingredients: catalog.ingredients.filter(i => usedIngredients.has(i.id)), products, drinkGroups: groups };
}
export function validateDraftUpdate(catalog: Catalog, previous: Catalog, selection: CatalogSelection[]): string[] {
  const scope = draftUpdateScope(catalog, selection);
  const old = draftUpdateScope(previous, selection);
  const names = validateSupplyNames({ ingredients: catalog.ingredients.filter(i => selection.some(s => s.collection === "ingredients" && s.id === i.id)), packaging: catalog.packaging.filter(i => selection.some(s => s.collection === "packaging" && s.id === i.id)), equipment: catalog.equipment.filter(i => selection.some(s => s.collection === "equipment" && s.id === i.id)) });
  return [...names, ...validatePreparation(scope), ...validateCoffee(scope, old, previous), ...validateDrinkGroups(scope)];
}
